import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutlineOutlined';
import { byWeight, partsFor, type RecipeLine } from './recipe';
import { decimalInput, skuPicker } from './ui';

const grams = (n: number) => `${Math.round(n * 100) / 100} g`;

/**
 * What a product is made of, as a table: weighed materials take parts of the piece weight and
 * show their share and grams per piece; counted ones (垫片) take so many per piece.
 */
export function RecipeTable(p: {
  lines: RecipeLine[];
  /** Items to pick from; those sold with the product are left out by the caller. */
  options: any[];
  unit: string;
  /** The piece weight in grams, once known. */
  unitWeight: number;
  disabled: boolean;
  max: number;
  onChange: (lines: RecipeLine[]) => void;
}) {
  const skuOf = (id: string) => p.options.find((s) => s.id === id);
  const parts = partsFor(p.lines, skuOf);
  const set = (i: number, patch: Partial<RecipeLine>) =>
    p.onChange(p.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const share = (line: RecipeLine) =>
    parts.total ? (parts.partsOf(line) / parts.total) * p.unitWeight : 0;
  const head = { fontSize: 12, py: 0.75, whiteSpace: 'nowrap' } as const;
  const add = (
    <Button
      size="small"
      startIcon={<Add />}
      disabled={p.disabled || p.lines.length >= p.max}
      sx={{ ml: -0.5 }}
      onClick={() => p.onChange([...p.lines, { skuId: '', ratio: '' }])}
    >
      添加原料
    </Button>
  );
  if (!p.lines.length) return <Box>{add}</Box>;
  return (
    <Box>
      <Box
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
      >
        <Table size="small" sx={{ tableLayout: 'fixed', bgcolor: 'background.paper' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={head}>原料</TableCell>
              <TableCell sx={{ ...head, width: 112 }}>配比</TableCell>
              <TableCell sx={{ ...head, width: 52 }} align="right">
                占比
              </TableCell>
              <TableCell sx={{ ...head, width: 76 }} align="right">
                单耗
              </TableCell>
              <TableCell sx={{ ...head, width: 40, px: 0.5 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {p.lines.map((line, i) => {
              const sku = skuOf(line.skuId);
              const weighed = byWeight(sku);
              const percent = weighed ? parts.percent(line) : null;
              return (
                <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={{ py: 0.75, pl: 1 }}>
                    <Autocomplete<any>
                      fullWidth
                      size="small"
                      options={p.options.filter(
                        (s) => s.id === line.skuId || !p.lines.some((l) => l.skuId === s.id),
                      )}
                      value={sku ?? null}
                      disabled={p.disabled}
                      {...skuPicker()}
                      onChange={(_, v) => set(i, { skuId: v?.id ?? '', ratio: '' })}
                      renderInput={(params) => <TextField {...params} placeholder="选择原料" />}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.75, px: 1 }}>
                    {!sku ? null : weighed && parts.lone ? (
                      <Typography sx={{ fontSize: 13, px: 1 }}>全部</Typography>
                    ) : (
                      <TextField
                        size="small"
                        value={line.ratio}
                        disabled={p.disabled}
                        placeholder={weighed ? '50' : '1'}
                        slotProps={{
                          htmlInput: {
                            inputMode: 'decimal',
                            'aria-label': `${sku.name} ${weighed ? '份数' : '每件用量'}`,
                          },
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                {weighed ? '份' : `${sku.unit}/${p.unit || '个'}`}
                              </InputAdornment>
                            ),
                          },
                        }}
                        onChange={(e) => set(i, { ratio: decimalInput(e.target.value, 4) })}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>
                    {percent != null ? `${percent}%` : sku ? '—' : null}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    {!sku
                      ? null
                      : weighed
                        ? p.unitWeight && percent != null
                          ? grams(share(line))
                          : '—'
                        : line.ratio
                          ? `${Number(line.ratio)} ${sku.unit}`
                          : '—'}
                  </TableCell>
                  <TableCell sx={{ px: 0.5 }}>
                    <Tooltip title="删除这行">
                      <span>
                        <IconButton
                          size="small"
                          disabled={p.disabled}
                          onClick={() => p.onChange(p.lines.filter((_, j) => j !== i))}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
        {add}
        {parts.weighed.length > 1 && parts.total > 0 && (
          <Typography color="text.secondary" sx={{ fontSize: 12.5, ml: 'auto' }}>
            共 {parts.total} 份{p.unitWeight ? ` · 合计 ${grams(p.unitWeight)}` : ''}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
